import type { LoadImpactSummary } from "../domain/types";
import { toSigFigs } from "../../../utils/format";

interface Props {
  portfolioName: string;
  summary: LoadImpactSummary;
}

const FUEL_LABEL: Record<string, string> = {
  COL: "Coal",
  NG: "Natural gas",
  OIL: "Oil",
  NUC: "Nuclear",
  WAT: "Hydro",
};

const FUEL_BAR: Record<string, string> = {
  COL: "bg-zinc-500",
  NG: "bg-amber-500",
  OIL: "bg-orange-700",
};

function fuelName(code: string): string {
  return FUEL_LABEL[code] ?? code;
}

function fmtUSD(n: number): string {
  const a = Math.abs(n);
  if (a >= 1e9) return `$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `$${(a / 1e3).toFixed(0)}K`;
  return `$${a.toFixed(0)}`;
}

function fmtMWh(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M MWh`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K MWh`;
  return `${n.toFixed(0)} MWh`;
}

function methodLabel(m?: string): string {
  if (m === "pinn") return "consequential marginal · PINN";
  if (m === "statistical") return "consequential marginal · statistical";
  return "consequential marginal";
}

/**
 * The headline for a grid-drawing load portfolio: the full
 * health / biodiversity / economic footprint of the electricity the load
 * consumed, and — the differentiator — which grid generators were ramped
 * to serve it. Consumption is framed as harm (no benefit/RE framing).
 */
export function LoadGridSupplyPanel({ portfolioName, summary }: Props) {
  const gs = summary.grid_supply;
  const meta = gs?.meta;
  const imported = meta?.grid_import_mwh ?? 0;
  if (!gs || imported <= 0) return null;

  const perFuel = meta?.per_fuel_mwh ?? {};
  const fuels = Object.entries(perFuel)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  const fuelTotal = fuels.reduce((s, [, v]) => s + v, 0) || 1;

  const stats = [
    {
      label: "Public-health harm",
      unit: "Disability-Adjusted Life Years",
      value: toSigFigs(gs.DALYs),
      accent: "text-red-400 border-l-red-400",
    },
    {
      label: "Biodiversity loss",
      unit: "Potentially Disappeared Fraction of species",
      value: toSigFigs(gs.PDFs),
      accent: "text-red-400 border-l-red-400",
    },
    {
      label: "Economic damage",
      unit: "USD, ReCiPe 2016 monetized",
      value: fmtUSD(gs.USD),
      accent: "text-red-400 border-l-red-400",
    },
  ];

  return (
    <div className="bg-surface-700 rounded-lg shadow border border-border overflow-hidden">
      <div className="px-6 pt-5 pb-1">
        <h2 className="text-lg font-medium text-text-primary">
          {portfolioName} — Grid-Supplied Electricity Footprint
        </h2>
      </div>
      <div className="px-6 pb-3">
        <p className="text-sm text-text-secondary">
          Operating this load drew{" "}
          <span className="text-text-primary font-medium">
            {fmtMWh(imported)}
          </span>{" "}
          from the grid. Full lifecycle impact — air, soil &amp; water, not
          carbon alone &middot; {methodLabel(meta?.marginal_method)}
          {meta?.analysis_year ? ` · ${meta.analysis_year} grid` : ""}
        </p>
      </div>

      {/* Footprint as harm */}
      <div className="grid grid-cols-3 gap-0 border-t border-border">
        {stats.map((s) => (
          <div key={s.label} className={`border-l-4 p-4 ${s.accent}`}>
            <p className="text-sm font-semibold uppercase tracking-wide text-red-400">
              {s.label}
              <span className="block font-normal normal-case tracking-normal text-text-muted mt-0.5">
                {s.unit}
              </span>
            </p>
            <p className="mt-2 text-2xl font-bold leading-tight text-red-400">
              {s.value}
            </p>
            <p className="text-[11px] text-text-muted">harm caused (annual)</p>
          </div>
        ))}
      </div>

      {/* Which generators served this load — the differentiator */}
      <div className="px-6 py-4 border-t border-border">
        <p className="text-sm font-semibold uppercase tracking-wide text-text-secondary mb-1">
          Which generators served this load
        </p>
        <p className="text-[11px] text-text-muted mb-3">
          Your load made the grid ramp these specific units at the margin —
          this is what your consumption actually caused.
        </p>
        <div className="space-y-2">
          {fuels.map(([code, mwh]) => {
            const pct = (mwh / fuelTotal) * 100;
            return (
              <div key={code} className="flex items-center gap-3">
                <span className="w-28 text-xs text-text-secondary">
                  {fuelName(code)}
                </span>
                <div className="flex-1 h-3 rounded bg-surface-600 overflow-hidden">
                  <div
                    className={`h-full ${FUEL_BAR[code] ?? "bg-slate-500"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="w-16 text-right text-xs text-text-primary font-medium">
                  {pct.toFixed(0)}%
                </span>
                <span className="w-24 text-right text-[11px] text-text-muted">
                  {fmtMWh(mwh)}
                </span>
              </div>
            );
          })}
          {fuels.length === 0 && (
            <p className="text-xs text-text-muted">
              No marginal fuel attribution available for this grid/period.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
